import moment from "moment";

// Turns the text of a Stock Transfer PDF (the challan the sending store prints)
// into line items for Stock IN. Kept free of pdf.js and API calls so it can be
// tested on its own: it works on plain { str, x0, x1, y } text runs per page.
//
// Every cell of the challan's table is its own text run, so a cell is put in
// the column whose header it sits under, not guessed from the order of words.

// Header text (upper-cased, spaces collapsed) → the field it fills
const COLUMN_ALIASES = {
  name: ["DESCRIPTION", "PRODUCT", "PRODUCT NAME", "ITEM", "ITEM NAME", "PARTICULARS"],
  rack: ["RACK", "LOCATION", "BIN"],
  batch: ["BATCH NO", "BATCH NO.", "BATCH", "BATCHNO"],
  pack: ["PACK", "PACKING"],
  exp: ["EXP", "EXPIRY", "EXP DATE", "EXP."],
  qty: ["QTY", "QUANTITY"],
  free: ["FRE", "FREE", "FREE QTY"],
  mrp: ["MRP"],
  rate: ["RATE", "PTR"],
  dis: ["DIS%", "DISC%", "DISC", "DIS", "DISC %"],
  gst: ["GST", "GST%", "GST %"],
  net: ["NET", "NET RATE"],
  amt: ["AMT", "AMOUNT"],
};
const ALIAS_TO_KEY = Object.entries(COLUMN_ALIASES).reduce(
  (acc, [key, aliases]) => {
    aliases.forEach((alias) => (acc[alias] = key));
    return acc;
  },
  {}
);

const ROW_TOLERANCE = 2.5;
const DATE_RE = /^\d{1,2}[-/ ][A-Za-z]{3}[-/ ]\d{2,4}$|^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}$/;

const cleanText = (str) => String(str ?? "").replace(/\s+/g, " ").trim();

// "1,234.50" / "5.0%" → number; "-" or blank → null
export const parsePdfNumber = (str) => {
  const s = cleanText(str).replace(/[,₹%]/g, "");
  if (!s || s === "-") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

// Pharma challans print expiry as month/year ("3/28") — stock is good till
// the end of that month, so that is the date the batch gets.
export const parseExpiryDate = (raw) => {
  const s = cleanText(raw);
  let m = s.match(/^(\d{1,2})[/\-.](\d{2}|\d{4})$/);
  if (m) {
    const month = Number(m[1]);
    const year = m[2].length === 2 ? 2000 + Number(m[2]) : Number(m[2]);
    if (month < 1 || month > 12) return "";
    return moment({ year, month: month - 1, day: 1 })
      .endOf("month")
      .format("YYYY-MM-DD");
  }
  m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2}|\d{4})$/);
  if (m) {
    const year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    const date = moment({ year, month: Number(m[2]) - 1, day: Number(m[1]) });
    return date.isValid() ? date.format("YYYY-MM-DD") : "";
  }
  const named = moment(s, ["MMM-YY", "MMM-YYYY", "MMM YY", "MMM YYYY"], true);
  return named.isValid() ? named.endOf("month").format("YYYY-MM-DD") : "";
};

// Group a page's text runs into visual rows, top of the page first
const groupRows = (items) => {
  const sorted = [...items].sort((a, b) => b.y - a.y || a.x0 - b.x0);
  const rows = [];
  sorted.forEach((item) => {
    const row = rows.find((r) => Math.abs(r.y - item.y) <= ROW_TOLERANCE);
    if (row) row.cells.push(item);
    else rows.push({ y: item.y, cells: [item] });
  });
  rows.forEach((row) => row.cells.sort((a, b) => a.x0 - b.x0));
  return rows;
};

// The table header row: the row naming at least the description, batch and
// qty columns. Returns its columns sorted left to right.
const findHeaderColumns = (rows) => {
  for (const row of rows) {
    const columns = [];
    row.cells.forEach((cell) => {
      const text = cleanText(cell.str).toUpperCase();
      const key = ALIAS_TO_KEY[text];
      const prev = columns[columns.length - 1];
      // "BATCH" and "NO" printed as two runs
      if (!key && prev?.key === "batch" && /^NO\.?$/.test(text)) {
        prev.x1 = cell.x1;
        return;
      }
      if (key && !columns.some((col) => col.key === key)) {
        columns.push({ key, x0: cell.x0, x1: cell.x1 });
      }
    });
    const keys = columns.map((col) => col.key);
    if (["name", "batch", "qty"].every((k) => keys.includes(k))) {
      return { y: row.y, columns: columns.sort((a, b) => a.x0 - b.x0) };
    }
  }
  return null;
};

// The column a cell sits under: most horizontal overlap with the header,
// else the nearest header. Anything left of the first column is the S.No.
const columnFor = (cell, columns) => {
  if (cell.x1 <= columns[0].x0 - 1) return "sno";
  let best = null;
  let bestOverlap = 0;
  columns.forEach((col) => {
    const overlap = Math.min(cell.x1, col.x1) - Math.max(cell.x0, col.x0);
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      best = col;
    }
  });
  if (best) return best.key;
  let nearest = columns[0];
  let nearestDistance = Infinity;
  columns.forEach((col) => {
    const distance =
      cell.x1 < col.x0 ? col.x0 - cell.x1 : Math.max(0, cell.x0 - col.x1);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = col;
    }
  });
  return nearest.key;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const buildLine = (cellsByKey) => {
  const text = (key) => cleanText(cellsByKey[key]);
  const name = text("name");
  const batch = text("batch").replace(/\s+/g, "");
  const qty = parsePdfNumber(text("qty"));
  const rate = parsePdfNumber(text("rate"));
  const sno = text("sno");
  let net = parsePdfNumber(text("net"));
  if (!name || !batch || !(qty > 0) || (net === null && rate === null)) {
    return null;
  }
  if (sno && !/^\d+$/.test(sno)) return null;

  const dis = parsePdfNumber(text("dis")) ?? 0;
  const gst = parsePdfNumber(text("gst")) ?? 0;
  // NET is the landed rate per qty (rate less discount, plus GST)
  if (net === null) net = round2(rate * (1 - dis / 100) * (1 + gst / 100));
  const amt = parsePdfNumber(text("amt")) ?? round2(net * qty);
  const baseRate = rate ?? round2(net / (1 + gst / 100) / (1 - dis / 100 || 1));

  return {
    sno: sno ? Number(sno) : null,
    name,
    rack: text("rack"),
    batch,
    pack: text("pack"),
    exp_raw: text("exp"),
    exp_date: parseExpiryDate(text("exp")),
    qty,
    free: parsePdfNumber(text("free")) ?? 0,
    mrp: parsePdfNumber(text("mrp")) ?? 0,
    rate: baseRate,
    dis,
    // Discount in money on the pre-tax rate, for the batch's Discount Amount
    dis_amount: round2((baseRate * qty * dis) / 100),
    gst,
    net,
    amt,
  };
};

// Document details printed above the table, best effort — used to show the
// user what was read and to cross-check the parsed lines against the footer.
const readMeta = (pages) => {
  // pages here hold only non-blank runs — blank spacer runs sit between
  // cells and would be taken as "the next cell"
  const all = pages.flatMap((page) => page.items);
  const firstPage = pages[0]?.items ?? [];
  const near = (a, b, dy) => Math.abs(a.y - b.y) <= dy;

  const dateCell = firstPage.find((c) => DATE_RE.test(cleanText(c.str)));
  const docNoCell = dateCell
    ? firstPage
        .filter(
          (c) =>
            c !== dateCell &&
            near(c, dateCell, 8) &&
            c.x1 <= dateCell.x0 &&
            /\d/.test(c.str) &&
            !/\s/.test(cleanText(c.str))
        )
        .sort((a, b) => b.x1 - a.x1)[0]
    : null;

  const titleCell = firstPage.find((c) =>
    /STOCK\s+TRANSFER/i.test(cleanText(c.str))
  );
  const fromCell = titleCell
    ? firstPage
        .filter(
          (c) =>
            c !== dateCell &&
            c !== docNoCell &&
            near(c, titleCell, 8) &&
            c.x0 >= titleCell.x1
        )
        .sort((a, b) => a.x0 - b.x0)[0]
    : null;

  let toName = "";
  const toCell = firstPage.find((c) => /^TO\s*,/i.test(cleanText(c.str)));
  if (toCell) {
    toName = cleanText(toCell.str).replace(/^TO\s*,\s*/i, "");
    if (!toName) {
      const next = firstPage
        .filter((c) => near(c, toCell, ROW_TOLERANCE) && c.x0 >= toCell.x1)
        .sort((a, b) => a.x0 - b.x0)[0];
      toName = cleanText(next?.str);
    }
  }

  const footerText = pages
    .flatMap((page) => groupRows(page.items))
    .map((row) => row.cells.map((c) => cleanText(c.str)).join(" "))
    .join("\n");
  const itemsCount = footerText.match(/ITEMS\s*:\s*(\d+)/i)?.[1];
  const totalQty = footerText.match(/TOTAL\s+QTY\s*:\s*(\d+)/i)?.[1];

  let netAmount = null;
  const netCell = all.find((c) => /^NET\s+AMT$/i.test(cleanText(c.str)));
  if (netCell) {
    const value = all
      .filter(
        (c) =>
          near(c, netCell, ROW_TOLERANCE) &&
          c.x0 > netCell.x1 &&
          parsePdfNumber(c.str) !== null
      )
      .sort((a, b) => a.x0 - b.x0)[0];
    netAmount = value ? parsePdfNumber(value.str) : null;
  }

  const docDate = dateCell
    ? moment(
        cleanText(dateCell.str),
        ["DD-MMM-YYYY", "DD MMM YYYY", "DD/MM/YYYY", "DD-MM-YYYY", "DD-MMM-YY"],
        true
      )
    : null;

  return {
    doc_no: cleanText(docNoCell?.str),
    doc_date: docDate?.isValid() ? docDate.format("YYYY-MM-DD") : "",
    from_name: cleanText(fromCell?.str),
    to_name: toName,
    items_count: itemsCount ? Number(itemsCount) : null,
    total_qty: totalQty ? Number(totalQty) : null,
    net_amount: netAmount,
  };
};

// pages: [{ items: [{ str, x0, x1, y }] }]
export const parseStockTransferPdf = (rawPages = []) => {
  const pages = rawPages.map((page) => ({
    items: (page?.items ?? []).filter((c) => cleanText(c.str)),
  }));
  const lines = [];
  let columns = null;
  pages.forEach((page) => {
    const rows = groupRows(page.items);
    const header = findHeaderColumns(rows);
    // A continuation page without its own header reuses the last one
    if (header) columns = header.columns;
    if (!columns) return;
    rows
      .filter((row) => !header || row.y < header.y - ROW_TOLERANCE)
      .forEach((row) => {
        const cellsByKey = {};
        row.cells.forEach((cell) => {
          const key = columnFor(cell, columns);
          cellsByKey[key] = cellsByKey[key]
            ? `${cellsByKey[key]} ${cell.str}`
            : cell.str;
        });
        const line = buildLine(cellsByKey);
        if (line) lines.push(line);
      });
  });
  return { meta: readMeta(pages), lines };
};

// ---- Matching a PDF description to an item master record ----

const ELLIPSIS_RE = /(…|\.{3})\s*$/;
export const isTruncatedName = (name) => ELLIPSIS_RE.test(cleanText(name));
export const stripEllipsis = (name) =>
  cleanText(name).replace(ELLIPSIS_RE, "").trim();
const normalizeName = (name) =>
  stripEllipsis(name)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
const compactName = (name) => normalizeName(name).replace(/ /g, "");

// Best item for a PDF description out of the search results.
// Returns { item, exact } or null when the user has to pick it.
export const pickBestItemMatch = (pdfName, candidates = []) => {
  const target = compactName(pdfName);
  if (!target || !candidates.length) return null;
  const exact = candidates.find((c) => compactName(c?.item_name) === target);
  if (exact) return { item: exact, exact: true };

  // A name cut short on the challan ("ULTRA MAGNESIUM 200M…")
  const startsWith = candidates.filter((c) =>
    compactName(c?.item_name).startsWith(target)
  );
  if (startsWith.length === 1) {
    return { item: startsWith[0], exact: isTruncatedName(pdfName) };
  }
  return null;
};
